import React, { useState } from 'react'
import {
  Select,
  SelectValue,
  SelectTrigger,
  SelectContent,
  SelectItem,
  Input,
  IconButton,
  Tooltip,
  TooltipTrigger,
  TooltipProvider,
  TooltipContent,
  Icon
} from '@shopflo/ui'
import { X, ArrowLineUp } from '@phosphor-icons/react'
import { useDiscountForm } from '../../../hooks/useDiscountForm'
import { usePrerequisiteProducts } from '../../../hooks/usePrerequisiteProducts'
import {
  DiscountApplyToType,
  PrerequisiteCatalogueType,
  StandardPrerequisite,
  PrerequisiteType,
  DiscountFieldPaths,
  PurchaseConditionAmountType
} from 'src/lib/types/discountsV2'

import FormErrorText from 'src/components/common/FormErrorText'
import { AndBadge, BxgyBadge } from '../../../common/ProductIcons'
import { classNames } from 'src/lib/utils/helpers'
import CatalogueGroupControl from '../common/CatalogueGroupControl'
import { useLocale } from 'src/lib/hooks/useLocal'
import { discountCatalogueErrors } from 'src/lib/utils/catalogueErrorHelpers'

const DiscountApplicationRulesBxgyBuyX = () => {
  const {
    state: { prerequisite: rawPrerequisite, errors },
    actions: { setFormState }
  } = useDiscountForm()
  const { t } = useLocale()

  const prerequisite = rawPrerequisite as StandardPrerequisite

  const { metaData: preMetaData, prerequisiteCatalogue } = prerequisite
  const [showMaxMap, setShowMaxMap] = useState<Record<number, boolean>>({})

  const buyXType =
    prerequisite.purchaseConditionAmountType === PurchaseConditionAmountType.SPECIFIC_COLLECTIONS
      ? DiscountApplyToType.SPECIFIC_COLLECTIONS
      : DiscountApplyToType.SPECIFIC_PRODUCTS

  // --- Derived State for Rendering ---
  const activePrereq = prerequisiteCatalogue?.[0]
  const currentType = activePrereq?.type || PrerequisiteType.QUANTITY
  const items = preMetaData?.prerequisiteCatalogue || []

  // If no items, we render one virtual empty group
  const displayGroups = items.length > 0 ? items : [[]]

  const {
    handleItemsSelected,
    handleRemoveGroup,
    handleRemoveItemFromGroup: handleRemoveItem,
    handleRemoveExcludedItem,
    handleVariantUpdate,
    handleAddEmptyGroup,
    handleValueChange
  } = usePrerequisiteProducts({
    prerequisite,
    setFormState,
    catalogueType: buyXType === DiscountApplyToType.SPECIFIC_COLLECTIONS ? 'COLLECTION' : 'PRODUCT_AND_VARIANT'
  })

  const handleBuyXTypeChange = (value: string) => {
    const newType = value as PrerequisiteType
    const newPrereq = [...prerequisiteCatalogue]

    if (newPrereq.length === 0) {
      if (newType === PrerequisiteType.QUANTITY) {
        newPrereq.push({ type: PrerequisiteType.QUANTITY, quantity: 1, entities: [] })
      } else {
        newPrereq.push({ type: PrerequisiteType.PRICE, price: 1, entities: [] })
      }
    } else {
      for (let i = 0; i < newPrereq.length; i++) {
        const entities = newPrereq[i].entities
        if (newType === PrerequisiteType.QUANTITY) {
          newPrereq[i] = { type: PrerequisiteType.QUANTITY, quantity: 1, entities }
        } else {
          newPrereq[i] = { type: PrerequisiteType.PRICE, price: 1, entities }
        }
      }
    }

    setFormState({
      prerequisite: {
        ...prerequisite,
        prerequisiteCatalogue: newPrereq
      }
    })
  }

  const handleRangeChange = (groupIndex: number, type: 'min' | 'max', value: string) => {
    handleValueChange(groupIndex, value, type)
  }

  const handleToggleMax = (groupIndex: number, show: boolean) => {
    setShowMaxMap(prev => ({ ...prev, [groupIndex]: show }))
    if (!show) {
      handleValueChange(groupIndex, '', 'max')
    }
  }

  const handleApplyToTypeChange = (value: string) => {
    const val = value

    const newEntity: PrerequisiteCatalogueType = {
      type: currentType,
      ...(currentType === PrerequisiteType.QUANTITY ? { quantity: 1 } : { price: 0 }),
      entities: []
    } as PrerequisiteCatalogueType

    setFormState(
      {
        prerequisite: {
          ...prerequisite,
          purchaseConditionAmountType:
            val === DiscountApplyToType.SPECIFIC_COLLECTIONS
              ? PurchaseConditionAmountType.SPECIFIC_COLLECTIONS
              : PurchaseConditionAmountType.SPECIFIC_PRODUCTS,
          prerequisiteCatalogue: [newEntity],
          metaData: {
            ...prerequisite.metaData,
            prerequisiteCatalogue: [[]]
          }
        }
      },
      [DiscountFieldPaths.PREREQUISITE_CATALOGUE]
    )
  }

  const renderList = () => {
    const getInputValue = (groupIndex: number) => {
      const item = prerequisiteCatalogue[groupIndex]
      if (!item) return currentType === PrerequisiteType.PRICE ? 0 : 1
      return (item.type === PrerequisiteType.PRICE ? item.price : item.quantity) ?? ''
    }

    const getMaxValue = (groupIndex: number) => {
      const item = prerequisiteCatalogue[groupIndex]
      if (!item) return undefined
      return item.type === PrerequisiteType.PRICE ? item.maxPrice : item.maxQuantity
    }

    return (
      <div className='flex flex-col gap-4'>
        {displayGroups.map((group, groupIndex) => {
          const maxValue = getMaxValue(groupIndex)
          const hasMax = showMaxMap[groupIndex] ?? (maxValue !== undefined && maxValue !== null)

          return (
            <div key={groupIndex}>
              <div className='rounded-xl flex flex-col gap-3 mb-3'>
                <div className='flex justify-between items-start'>
                  <div className='flex flex-col gap-3 w-full'>
                    <div className='flex items-center gap-2 flex-wrap w-fit'>
                      {groupIndex === 0 && <BxgyBadge label='X' />}
                      {groupIndex === 0 ? (
                        <span className='text-text-sm-semibold text-zinc-800'>{t('discounts_v2.if_shopper_buys')}</span>
                      ) : (
                        <div className='flex items-center gap-2'>
                          <AndBadge />
                          <span className='text-text-sm-semibold text-zinc-800'>{t('discounts_v2.buys')}</span>
                        </div>
                      )}

                      <div className='flex items-center gap-2 w-fit'>
                        <div className='flex items-center gap-0 w-fit'>
                          <Input
                            id={`buy-x-min-input-group-${groupIndex}`}
                            type='number'
                            value={getInputValue(groupIndex)}
                            onChange={e => handleRangeChange(groupIndex, 'min', e.target.value)}
                            onFocus={e => e.target.select()}
                            dimension='sm'
                            variant='filled'
                            className={classNames('rounded-r-none', 'min-w-7')}
                            startAdornment={
                              <span className='text-xs text-zinc-500 pr-1'>{t('discounts_v2.min_label')}</span>
                            }
                            style={{
                              width: `${(String(getInputValue(groupIndex) || '').length || 0) + 2}ch`
                            }}
                            error={!!errors[`${DiscountFieldPaths.PREREQUISITE_CATALOGUE}.${groupIndex}.quantity`]}
                            placeholder='0'
                          />

                          {!hasMax && (
                            <TooltipProvider>
                              <Tooltip>
                                <TooltipTrigger asChild>
                                  <div
                                    id={`buy-x-max-toggle-group-${groupIndex}`}
                                    onClick={() => handleToggleMax(groupIndex, true)}
                                    className="flex h-7 cursor-pointer items-center justify-center whitespace-nowrap rounded-r-[8px] rounded-l-none bg-zinc-400/10 px-2 transition-colors hover:bg-zinc-400/15 relative before:absolute before:left-0 before:top-1/2 before:-translate-y-1/2 before:h-4 before:w-[1px] before:bg-zinc-300 before:content-['']"
                                  >
                                    <Icon IconComponent={ArrowLineUp} size='xs' className='text-zinc-500' />
                                  </div>
                                </TooltipTrigger>
                                <TooltipContent>{t('discounts_v2.set_max_limit')}</TooltipContent>
                              </Tooltip>
                            </TooltipProvider>
                          )}

                          {hasMax && (
                            <Input
                              id={`buy-x-max-input-group-${groupIndex}`}
                              type='number'
                              value={maxValue ?? ''}
                              onChange={e => handleRangeChange(groupIndex, 'max', e.target.value)}
                              onFocus={e => {
                                e.target.select()
                                handleToggleMax(groupIndex, true)
                              }}
                              className='rounded-l-none  max-w-36 min-w-fit'
                              dimension='sm'
                              variant='filled'
                              startAdornment={
                                <span className='text-xs text-zinc-500 pr-1'>{t('discounts_v2.upto_label')}</span>
                              }
                              placeholder='∞'
                              style={{ width: `${(String(maxValue ?? '').length || 1) + 2}ch`, minWidth: '4ch' }}
                              endAdornment={
                                <IconButton
                                  IconComponent={X}
                                  size='xs'
                                  variant='ghost'
                                  onClick={() => handleToggleMax(groupIndex, false)}
                                  className='bg-transparent text-zinc-400 hover:text-zinc-600'
                                />
                              }
                            />
                          )}
                        </div>
                        {groupIndex > 0 ? (
                          <span className='text-text-sm-semibold text-zinc-500'>
                            {currentType === PrerequisiteType.QUANTITY
                              ? t('discounts_v2.quantity_of')
                              : t('discounts_v2.worth_of')}
                          </span>
                        ) : (
                          <Select value={currentType} onValueChange={handleBuyXTypeChange}>
                            <SelectTrigger
                              id='buy-x-type-trigger'
                              className='border-0 p-0 h-auto focus:ring-0 shadow-none bg-transparent hover:bg-transparent text-zinc-500 text-text-sm-regular flex gap-1 items-center w-auto'
                              showCaret
                            >
                              <span className='text-text-sm-semibold text-zinc-600 transition-colors group-hover:text-zinc-900'>
                                <SelectValue />
                              </span>
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value={PrerequisiteType.QUANTITY}>{t('discounts_v2.quantity_of')}</SelectItem>
                              <SelectItem value={PrerequisiteType.PRICE}>{t('discounts_v2.worth_of')}</SelectItem>
                            </SelectContent>
                          </Select>
                        )}
                      </div>
                    </div>

                    <FormErrorText
                      message={
                        errors[`${DiscountFieldPaths.PREREQUISITE_CATALOGUE}.${groupIndex}.quantity`] ||
                        errors[`${DiscountFieldPaths.PREREQUISITE_CATALOGUE}.${groupIndex}.price`] ||
                        errors[`${DiscountFieldPaths.PREREQUISITE_CATALOGUE}.${groupIndex}.maxPrice`] ||
                        errors[`${DiscountFieldPaths.PREREQUISITE_CATALOGUE}.${groupIndex}.maxQuantity`]
                      }
                    />

                    {groupIndex === 0 && (
                      <div className='flex gap-6'>
                        <Select value={buyXType} onValueChange={handleApplyToTypeChange}>
                          <SelectTrigger className=' bg-white rounded-lg h-9 w-[50%]'>
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value={DiscountApplyToType.SPECIFIC_PRODUCTS}>
                              {t('discounts_v2.specific_products_label')}
                            </SelectItem>
                            <SelectItem value={DiscountApplyToType.SPECIFIC_COLLECTIONS}>
                              {t('discounts_v2.specific_collections_label')}
                            </SelectItem>
                          </SelectContent>
                        </Select>
                        <div className='w-[50%]'></div>
                      </div>
                    )}
                  </div>

                  {groupIndex > 0 && (
                    <div className='flex items-center gap-2 text-nowrap'>
                      <span className='text-text-xs-medium text-zinc-500'>
                        {t('discounts_v2.group_count', { count: groupIndex + 1 })}
                      </span>
                      <IconButton
                        onClick={() => handleRemoveGroup(groupIndex)}
                        variant='ghost'
                        size='xs'
                        IconComponent={X}
                        className='text-zinc-500'
                      />
                    </div>
                  )}
                </div>

                <div className='flex w-full mb-3'>
                  <div className='border-l-1 border-zinc-200 w-5 rounded-l-lg border-y-1 h-auto'></div>
                  <div className='flex flex-col rounded-lg px-2 w-full'>
                    <CatalogueGroupControl
                      groupIndex={groupIndex}
                      includedGroups={displayGroups}
                      excludedGroups={prerequisite.metaData?.excludedCatalogue}
                      catalogueType={
                        buyXType === DiscountApplyToType.SPECIFIC_COLLECTIONS ? 'COLLECTION' : 'PRODUCT_AND_VARIANT'
                      }
                      actions={{
                        onRemoveIncludedItem: handleRemoveItem,
                        onRemoveExcludedItem: handleRemoveExcludedItem,
                        onUpdateItem: handleVariantUpdate,
                        onItemsSelected: handleItemsSelected,
                        onAddEmptyGroup: handleAddEmptyGroup
                      }}
                      errorHandling={{
                        errors: discountCatalogueErrors(errors, DiscountFieldPaths.PREREQUISITE_CATALOGUE)
                      }}
                      config={{
                        showVariantEditor: true,
                        addText: t('discounts_v2.add_product_label'),
                        showExclusionOption: true,
                        emptyText:
                          buyXType === DiscountApplyToType.SPECIFIC_COLLECTIONS
                            ? t('discounts_v2.choose_collections')
                            : t('discounts_v2.choose_products')
                      }}
                    />
                  </div>
                  <div className='border-r-1 border-zinc-200 w-5 rounded-r-lg border-y-1 h-auto'></div>
                </div>
              </div>
            </div>
          )
        })}
      </div>
    )
  }

  return (
    <div className='flex w-full mb-3'>
      <div className='flex flex-col gap-2 rounded-lg w-full'>
        {renderList()}
        <FormErrorText message={errors[DiscountFieldPaths.PREREQUISITE_CATALOGUE]} />
      </div>
    </div>
  )
}

export default DiscountApplicationRulesBxgyBuyX
